import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { House, Grains, Cow, Barn, ListChecks, CurrencyCircleDollar, FileText, Binoculars, Tractor, ShieldCheck, GearSix, CalendarBlank, Plant,
  DotsThreeOutline, Bell, X } from "@phosphor-icons/react";
import { useWorkspace, useApi, nzDateTime } from "@/lib/api";
import { Modal } from "@/components/app/common";
import { ChatGPTBadge } from "@/components/app/ChatGPTConnector";

export const NAV = [
  { to: "/", label: "Dashboard", Icon: House, end: true },
  { to: "/calendar", label: "Calendar", Icon: CalendarBlank },
  { to: "/feeding", label: "Feeding", Icon: Grains, module: "feeding" },
  { to: "/pasture", label: "Pasture", Icon: Plant },
  { to: "/livestock", label: "Livestock", Icon: Cow, module: "livestock" },
  { to: "/grazing", label: "Grazing", Icon: Barn, module: "grazing" },
  { to: "/tasks", label: "Tasks & Orders", Icon: ListChecks, module: "tasks" },
  { to: "/finances", label: "Finances", Icon: CurrencyCircleDollar, module: "finances", perm: "finance_view" },
  { to: "/reporting", label: "Reporting", Icon: FileText, module: "reporting" },
  { to: "/inspections", label: "Inspections & Visits", Icon: Binoculars, module: "inspections" },
  { to: "/machinery", label: "Machinery & Maintenance", Icon: Tractor, module: "machinery" },
  { to: "/compliance", label: "Compliance & Documents", Icon: ShieldCheck, module: "compliance" },
  { to: "/settings", label: "Settings", Icon: GearSix, perm: "manage_settings" },
];

function useNavItems() {
  const { farm, farms, can } = useWorkspace();
  return NAV.filter((n) => {
    if (n.perm && !can(n.perm)) return false;
    if (!n.module) return true;
    if (farm) return farm.effective_modules?.[n.module];
    return farms.some((f) => f.effective_modules?.[n.module]);
  });
}

function Scope({ p = "" }) {
  const { groups, groupId, setGroupId, farms, farmId, setFarmId, multiGroup, user } = useWorkspace();
  const consolidated = ["admin", "executive", "operations"].includes(user?.role);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {multiGroup && (
        <select data-testid={`${p}group-switcher`} value={groupId} onChange={(e) => setGroupId(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm font-medium">
          {consolidated && <option value="all">All authorised groups</option>}
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}
      {farms.length > 1 ? (
        <select data-testid={`${p}farm-switcher`} value={farmId} onChange={(e) => setFarmId(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm font-medium">
          <option value="all">All farms</option>
          {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      ) : farms[0] ? <span className="text-sm font-medium text-slate-700" data-testid={`${p}farm-name`}>{farms[0].name}</span> : null}
    </div>
  );
}

function Notifications() {
  const api = useApi();
  const nav = useNavigate();
  const { me, refresh } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const show = async () => { setItems(await api.get("/notifications")); setOpen(true); };
  const go = async (n) => { await api.post("/notifications/read", { id: n.id }); setOpen(false); refresh(); nav(n.link); };
  return (
    <>
      <button onClick={show} className="relative h-10 w-10 grid place-items-center rounded-md hover:bg-slate-100" data-testid="notifications-button" aria-label="Notifications">
        <Bell size={22} />
        {me?.unread > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center px-1" data-testid="notifications-count">{me.unread}</span>}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Notifications">
        <div className="flex justify-end"><button className="text-xs underline" data-testid="notifications-read-all" onClick={async () => { await api.post("/notifications/read", {}); refresh(); setItems(items.map((i) => ({ ...i, read: true }))); }}>Mark all read</button></div>
        <ul className="divide-y divide-slate-100">
          {items.length === 0 && <li className="py-4 text-sm text-slate-500">No notifications yet.</li>}
          {items.map((n) => (
            <li key={n.id}><button onClick={() => go(n)} className="w-full text-left py-3 row-hover px-2 rounded" data-testid={`notification-${n.id}`}>
              <p className={`text-sm ${n.read ? "text-slate-600" : "font-semibold"}`}>{n.title}</p>
              <p className="text-xs text-slate-500">{n.body} · {nzDateTime(n.at)}</p>
            </button></li>))}
        </ul>
      </Modal>
    </>
  );
}

function SideNav({ items, onNavigate }) {
  return (
    <nav className="space-y-1">
      {items.map(({ to, label, Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${to === "/" ? "dashboard" : to.slice(1)}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 h-11 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
          <Icon size={20} />{label}
        </NavLink>))}
    </nav>
  );
}

export default function Layout() {
  const { group } = useWorkspace();
  const items = useNavItems();
  const [more, setMore] = useState(false);
  const accent = group?.accent_color || "#166534";
  const brand = group?.name || "TCG Agriculture";
  const mobileMain = items.filter((i) => ["/", "/tasks", "/livestock", "/feeding"].includes(i.to)).slice(0, 4);
  return (
    <div className="min-h-screen flex" style={{ "--group-accent": accent }}>
      <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-[#0F172A] text-white fixed inset-y-0 left-0">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            {group?.logo_url ? <img src={group.logo_url} alt="" className="h-9 w-9 rounded object-cover bg-white" /> :
              <span className="h-9 w-9 rounded grid place-items-center font-display font-bold" style={{ background: accent }}>{brand[0]}</span>}
            <div>
              <p className="font-display font-bold leading-tight" data-testid="workspace-brand">{brand}</p>
              <p className="text-[11px] text-slate-400">Farm Operations</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4"><SideNav items={items} /></div>
        <p className="px-6 py-4 text-[11px] text-slate-500 border-t border-white/10" data-testid="powered-by">Powered by TCG Agriculture</p>
      </aside>
      <div className="flex-1 lg:ml-72 min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="h-1" style={{ background: accent }} />
          <div className="flex items-center justify-between gap-3 px-4 sm:px-8 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="lg:hidden font-display font-bold truncate" data-testid="mobile-brand">{brand}</span>
              <div className="hidden sm:block"><Scope /></div>
            </div>
            <div className="flex items-center gap-2"><ChatGPTBadge /><Notifications /><UserButton /></div>
          </div>
          <div className="sm:hidden px-4 pb-3"><Scope p="mobile-" /></div>
        </header>
        <main className="px-4 sm:px-8 py-8 pb-28 lg:pb-12 max-w-7xl"><Outlet /></main>
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0F172A] text-white grid grid-cols-5 border-t border-white/10" data-testid="mobile-nav">
        {mobileMain.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`mobile-nav-${to === "/" ? "dashboard" : to.slice(1)}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-1 h-16 text-[11px] ${isActive ? "text-white" : "text-slate-400"}`}>
            <Icon size={22} />{label.split(" ")[0]}
          </NavLink>))}
        <button onClick={() => setMore(true)} className="flex flex-col items-center justify-center gap-1 h-16 text-[11px] text-slate-400" data-testid="mobile-nav-more" aria-label="More menu">
          <DotsThreeOutline size={22} />More
        </button>
      </nav>
      {more && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#0F172A] text-white p-6 overflow-y-auto" data-testid="mobile-more-menu">
          <div className="flex items-center justify-between mb-6">
            <p className="font-display font-bold">{brand}</p>
            <button onClick={() => setMore(false)} className="h-11 w-11 grid place-items-center" aria-label="Close menu" data-testid="mobile-more-close"><X size={24} /></button>
          </div>
          <SideNav items={items} onNavigate={() => setMore(false)} />
          <p className="mt-8 text-[11px] text-slate-500">Powered by TCG Agriculture</p>
        </div>)}
    </div>
  );
}
